'use client';

import { useEffect, useRef, RefObject } from 'react';

import { exitFullscreen, requestFullscreen } from '../utils';

const FULLSCREEN_VIEWPORT_HEIGHT_VAR = '--fullscreen-viewport-height';
const FULLSCREEN_HEIGHT_CLASS = 'fullscreen-viewport-height';
const FULLSCREEN_OVERFLOW_CLASS = 'fullscreen-overflow-hidden';
const ORIENTATION_CHANGE_DELAY = 100;

/**
 * -----------------------------------------------------------------------------
 * Custom hook that handles fullscreen mode with mobile viewport support.
 * Requests fullscreen API and applies dynamic viewport height styles to handle
 * mobile browser UI elements (address bar, etc.) that can cause bottom spacing.
 * Automatically cleans up on unmount.
 */
export function useFullscreen<T extends HTMLElement = HTMLElement>(
  containerRef?: RefObject<T | null>
): void {
  const orientationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    const containerElement = containerRef?.current || null;
    const { body } = document;
    const { documentElement: html } = document;

    const updateViewportHeight = () => {
      const viewportHeight = CSS.supports('height', '100dvh')
        ? '100dvh'
        : `${window.innerHeight}px`;

      html.style.setProperty(FULLSCREEN_VIEWPORT_HEIGHT_VAR, viewportHeight);
    };

    const applyFullscreenStyles = () => {
      const container = containerElement || html;

      updateViewportHeight();

      container.classList.add(FULLSCREEN_HEIGHT_CLASS);
      body.classList.add(FULLSCREEN_HEIGHT_CLASS, FULLSCREEN_OVERFLOW_CLASS);
      html.classList.add(FULLSCREEN_HEIGHT_CLASS, FULLSCREEN_OVERFLOW_CLASS);
    };

    const removeFullscreenStyles = () => {
      const container = containerElement || html;

      container.classList.remove(FULLSCREEN_HEIGHT_CLASS);
      body.classList.remove(FULLSCREEN_HEIGHT_CLASS, FULLSCREEN_OVERFLOW_CLASS);
      html.classList.remove(FULLSCREEN_HEIGHT_CLASS, FULLSCREEN_OVERFLOW_CLASS);

      html.style.removeProperty(FULLSCREEN_VIEWPORT_HEIGHT_VAR);
    };

    const handleResize = () => {
      updateViewportHeight();
    };

    const handleOrientationChange = () => {
      if (orientationTimeoutRef.current) {
        clearTimeout(orientationTimeoutRef.current);
      }

      orientationTimeoutRef.current = setTimeout(() => {
        updateViewportHeight();
        orientationTimeoutRef.current = null;
      }, ORIENTATION_CHANGE_DELAY);
    };

    requestFullscreen();

    applyFullscreenStyles();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (orientationTimeoutRef.current) {
        clearTimeout(orientationTimeoutRef.current);
        orientationTimeoutRef.current = null;
      }

      removeFullscreenStyles();

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);

      exitFullscreen();
    };
  }, [containerRef]);
}
